import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vocabularyService } from '../services/vocabulary.service';
import type { AddVocabularyWordPayload, AddManualVocabularyWordPayload, UpdateVocabularyReviewPayload, UpdateVocabularyWordPayload, GenerateVocabPayload, SaveVocabPayload, LinkExistingVocabularyPayload } from '../types/api.types';
import toast from 'react-hot-toast';

export const useVocabularyWords = (projectId?: number, language?: string) => {
  return useQuery({
    queryKey: ['vocabulary', 'words', projectId, language],
    queryFn: () => vocabularyService.getWords(projectId, language),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const useDueWords = (language?: string) => {
  return useQuery({
    queryKey: ['vocabulary', 'due', language],
    queryFn: () => vocabularyService.getDueWords(language),
    staleTime: 1000 * 60, // Cache for 1 minute
  });
};

export const useVocabularyStats = (projectId?: number, language?: string) => {
  return useQuery({
    queryKey: ['vocabulary', 'stats', projectId, language],
    queryFn: () => vocabularyService.getStats(projectId, language),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

export const useAddVocabularyWord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: AddVocabularyWordPayload) => vocabularyService.addWord(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to add word to vocabulary');
    },
  });
};

export const useAddManualVocabularyWord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: AddManualVocabularyWordPayload) => vocabularyService.addManualWord(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
      toast.success('Flashcard created!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create flashcard');
    },
  });
};

export const useReviewVocabularyWord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: UpdateVocabularyReviewPayload) => vocabularyService.reviewWord(payload),
    onSuccess: () => {
      // Force immediate refetch of stats so button updates right away
      queryClient.invalidateQueries({ 
        queryKey: ['vocabulary', 'stats'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['vocabulary', 'words']
      });
      
      // Mark due words as stale but DON'T refetch to prevent card flashing during review
      queryClient.invalidateQueries({ 
        queryKey: ['vocabulary', 'due'],
        refetchType: 'none'
      });
      
      // No toast - reviews happen in succession, would be annoying
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to record review');
    },
  });
};

export const useUpdateVocabularyWord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ wordId, payload }: { wordId: number; payload: UpdateVocabularyWordPayload }) => 
      vocabularyService.updateWord(wordId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
      toast.success('Word translation updated');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update word');
    },
  });
};

export const useDeleteVocabularyWord = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (wordId: number) => vocabularyService.deleteWord(wordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
      toast.success('Word removed from vocabulary');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete word');
    },
  });
};

export const useVocabularyBySegment = (segmentId: number) => {
  return useQuery({
    queryKey: ['vocabulary', 'segment', segmentId],
    queryFn: () => vocabularyService.getBySegment(segmentId),
    enabled: !!segmentId,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
  });
};

/**
 * Scans the current segment's transcript for words already in the user's
 * vocabulary (same source language) and links any unlinked matches to the
 * segment. Invalidates the vocabulary cache so HighlightedText picks up the
 * new links immediately.
 */
export const useLinkExistingVocabulary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LinkExistingVocabularyPayload) =>
      vocabularyService.linkExistingToSegment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to mark vocabulary words');
    },
  });
};

export const useRefreshVocabularyContext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (wordId: number) => vocabularyService.refreshContext(wordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
      toast.success('Context refreshed from current transcript');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to refresh context');
    },
  });
};

/**
 * Mutation hook to generate AI vocabulary suggestions for an episode.
 * Usage: const { mutate: generate, isPending, data, error } = useGenerateVocabulary();
 */
export const useGenerateVocabulary = () => {
  return useMutation({
    mutationFn: (payload: GenerateVocabPayload) => vocabularyService.generateVocab(payload),
    // No onSuccess invalidation — generate returns transient data, not stored state.
    // Errors displayed inline in panel — panel checks error.response?.status === 429 for rate limit.
  });
};

/**
 * Mutation hook to save accepted AI vocabulary cards.
 * Invalidates vocabulary query on success so the existing vocab list stays fresh.
 * Usage: const { mutate: save, isPending } = useSaveVocabularyCards();
 */
export const useSaveVocabularyCards = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveVocabPayload) => vocabularyService.saveVocabCards(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vocabulary'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to save vocabulary cards');
    },
  });
};
